import React, { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { AdjustmentsHorizontalIcon, ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { focusRing, useMediaQuery } from "../../pages/Exams/TestBuilder/ui";

const Badge = ({ count }) =>
  count > 0 ? (
    <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-blue-600 px-1.5 text-xs font-semibold tabular-nums text-white">
      {count}
    </span>
  ) : null;

/**
 * The filters card of a list. The search (`searchPlaceholder` and `searchLabel` default to the question
 * lists' texts) and the `filters` ({key, node, active}) are always on screen (on phones the filters move under
 * the toggle too); `more` ({key, node, active}) open with the "More filters" toggle, a quiet button from sm up (a
 * framed one next to the search on phones). `chips` ({key, label, value, onRemove}) name the active filters that
 * are not on screen; "Clear all" shows while `canClear`. Phones get 44px controls.
 */
export default function FilterPanel({
  search,
  onSearchChange,
  searchPlaceholder,
  searchLabel,
  filters = [],
  more = [],
  chips = [],
  canClear = false,
  onClearAll,
}) {
  const [t] = useTranslation();
  const wide = useMediaQuery("(min-width: 640px)");
  const [open, setOpen] = useState(false);
  const id = useId();
  const visible = wide ? filters : [];
  const hidden = wide ? more : [...filters, ...more];
  const hiddenActive = hidden.filter((f) => f.active).length;

  return (
    <section aria-label={t("question_list.filters_label")} className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
      {/* Wide screens: a grid of equal columns, the search two of them wide. */}
      <div className={wide ? "grid grid-cols-[repeat(auto-fill,minmax(13rem,1fr))] items-center gap-3" : "flex items-center gap-2"}>
        <div className={`relative min-w-0 ${wide ? "col-span-2" : "flex-1"}`}>
          <label htmlFor={`${id}-search`} className="sr-only">
            {searchLabel || t("question_list.search_label")}
          </label>
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <input
            id={`${id}-search`}
            type="search"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder || t("question_list.search_placeholder")}
            className="h-11 w-full text-ellipsis rounded-lg border-slate-300 pl-9 pr-3 text-sm text-slate-900 shadow-sm transition duration-200 placeholder:text-slate-500 hover:border-slate-400 focus:border-blue-600 focus:ring-4 focus:ring-blue-600/15 sm:h-10"
          />
        </div>
        {visible.map((f) => (
          <div key={f.key} className="min-w-0">
            {f.node}
          </div>
        ))}
        {hidden.length > 0 && (
          <button
            type="button"
            aria-expanded={open}
            aria-controls={`${id}-more`}
            onClick={() => setOpen((value) => !value)}
            className={`inline-flex h-11 shrink-0 items-center gap-2 justify-self-start whitespace-nowrap rounded-lg px-3.5 text-sm font-medium transition duration-200 sm:h-10 sm:px-3 ${focusRing} ${
              open
                ? "bg-slate-100 text-slate-900 max-sm:ring-1 max-sm:ring-inset max-sm:ring-slate-300"
                : "text-slate-600 hover:bg-slate-100 hover:text-slate-900 max-sm:bg-white max-sm:text-slate-700 max-sm:shadow-sm max-sm:ring-1 max-sm:ring-inset max-sm:ring-slate-300"
            }`}
          >
            <AdjustmentsHorizontalIcon className="h-4 w-4" aria-hidden="true" />
            {wide ? t("question_list.more_filters") : t("question_list.filters_short")}
            <Badge count={hiddenActive} />
            <ChevronDownIcon className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
          </button>
        )}
      </div>

      {hidden.length > 0 && open && (
        <div
          id={`${id}-more`}
          className={`mt-3 grid border-t border-slate-100 pt-3 ${wide ? "grid-cols-[repeat(auto-fill,minmax(13rem,1fr))] gap-3" : "grid-cols-1 gap-2"}`}
        >
          {hidden.map((f) => (
            <div key={f.key} className="min-w-0">
              {f.node}
            </div>
          ))}
        </div>
      )}

      {(chips.length > 0 || canClear) && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {chips.length > 0 && <span className="text-sm text-slate-500">{t("question_list.active_filters")}</span>}
          {chips.map((chip) => (
            <span
              key={chip.key}
              className="inline-flex min-w-0 max-w-full items-center gap-1 rounded-full bg-blue-50 py-0.5 pl-3 pr-0.5 text-sm font-medium text-blue-800 ring-1 ring-inset ring-blue-600/15"
            >
              <span className="truncate">
                {chip.label}: {chip.value}
              </span>
              {/* Phones: the × answers taps 8px around it (44px), the chip keeps its size. */}
              <button
                type="button"
                onClick={chip.onRemove}
                aria-label={t("question_list.remove_filter", { label: chip.label })}
                title={t("question_list.remove_filter", { label: chip.label })}
                className={`relative inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-blue-700 transition duration-200 before:absolute before:-inset-2 before:content-[''] hover:bg-blue-100 hover:text-blue-900 sm:before:hidden ${focusRing}`}
              >
                <XMarkIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            </span>
          ))}
          {canClear && (
            <button
              type="button"
              onClick={onClearAll}
              className={`inline-flex min-h-[2.75rem] items-center rounded-md px-1.5 py-1 text-sm font-medium text-slate-600 underline decoration-slate-300 underline-offset-4 transition duration-200 hover:text-slate-900 hover:decoration-slate-500 sm:min-h-0 ${focusRing}`}
            >
              {t("question_list.clear_all")}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
